import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import Toast from 'vue-toast-notification'
import { VueTolgee } from '@tolgee/vue'
import { createHead } from '@unhead/vue'
import GsComponents from '@getgreenspark/gs-components'

import vuetify from '@/plugins/vuetify'
import tolgee from '@/plugins/tolgee'

import 'vue3-carousel/dist/carousel.css'
import 'vue-toast-notification/dist/theme-bootstrap.css'
import '@/assets/styles/main.css'
import '@getgreenspark/gs-components/gs-components.css'

import App from './App.vue'
import router from './router'
import { registerSW } from 'virtual:pwa-register'

const app = createApp(App)
const head = createHead()

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENVIRONMENT,
  enabled: ['public-dev.getmads.com', 'public.getgreenspark.com'].includes(
    window.location.hostname,
  ),
  tracesSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  tracePropagationTargets: [
    'localhost',
    'public-dev.getmads.com',
    'public.getgreenspark.com',
    /^\//,
  ],
  ignoreErrors: [/Unable to preload CSS/i],
  beforeBreadcrumb(breadcrumb) {
    if (
      breadcrumb.category === 'console' &&
      /Unable to preload CSS/i.test(breadcrumb.message || '')
    ) {
      return null
    }
    return breadcrumb
  },
})

app
  .use(VueTolgee, { tolgee })
  .use(GsComponents)
  .use(vuetify)
  .use(createPinia())
  .use(router)
  .use(Toast)
  .use(head)
  .mount('#app')

if (import.meta.env.PROD) {
  const updateSW = registerSW({
    immediate: false,
    onNeedRefresh() {
      const key = 'pwa_refresh_done'
      const already = sessionStorage.getItem(key) === '1'
      if (!already) {
        sessionStorage.setItem(key, '1')
        updateSW(true)
      }
    },
    onRegistered() {},
    onOfflineReady() {},
  })
}

let firebaseInitialized = false
let analyticsInitialized = false

router.beforeEach((to, from, next) => {
  const isUnauthenticatedRoute = !to.matched.some((record) => record.meta.requiresAuth)

  // Initialize Firebase for editor routes (includes VueFire, but NOT Analytics)
  if (!firebaseInitialized && to.path.includes('/editor')) {
    firebaseInitialized = true
    Promise.all([import('vuefire'), import('@/auth/firebase.js')])
      .then(([{ VueFire }, { firebaseApp }]) => {
        app.use(VueFire, { firebaseApp })
        next()
      })
      .catch(() => {
        next()
      })
  } else if (!analyticsInitialized && isUnauthenticatedRoute) {
    // Lazy-load Analytics for unauthenticated routes (public pages) only

    analyticsInitialized = true
    import('@/auth/firebase.js')
      .then(({ initializeAnalytics }) => {
        initializeAnalytics()
        next()
      })
      .catch(() => {
        next()
      })
  } else {
    next()
  }
})
